import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import html2canvas from 'html2canvas';
import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import LabelQR from './LabelQR';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdfjs-worker.min.mjs`;

// The label keeps the same proportion to the page as before: it spans 255/612 of the
// page width (≈41%), and the LabelQR template is 450x110, so its aspect is 450:110.
const LABEL_WIDTH_RATIO = 255 / 612;
const LABEL_ASPECT = 110 / 450;

// Cap how wide we render each page on screen so very large pages stay manageable.
const MAX_RENDER_WIDTH = 700;

const FiledInsertQR = forwardRef(({
  fileUrl, labelQRRef, handleFileUpload, setLoading, onFileLoaded, filed
}, ref) => {
  const containerRef = useRef(null);
  const stageRefs = useRef([]);
  const canvasRefs = useRef([]);
  // Raw PDF bytes, kept so we can stamp the exact document we rendered (no refetch).
  const pdfBytesRef = useRef(null);
  // pdfjs page proxies, kept between the "measure" and "paint" effects.
  const pageProxiesRef = useRef([]);

  // One entry per page: { scale, pageWidthPt, pageHeightPt, canvasW, canvasH }
  const [pages, setPages] = useState([]);
  const [rendering, setRendering] = useState(true);
  const [activePage, setActivePage] = useState(0);
  const [position, setPosition] = useState({ top: 10, left: 10 });
  const [dragging, setDragging] = useState(false);
  const grabOffset = useRef({ x: 0, y: 0 });

  // Render the real label once; dragging only moves the wrapper, not the QR.
  const labelPreview = useMemo(() => <LabelQR filed={filed || {}} />, [filed]);

  const active = pages[activePage];
  // On-screen label box derived from the active page (px).
  const labelW = active ? active.canvasW * LABEL_WIDTH_RATIO : 0;
  const labelH = labelW * LABEL_ASPECT;

  // Rebuild the cache-busted Firebase URL the same way the old code did.
  const freshUrl = useMemo(() => {
    if (!fileUrl) return null;
    const parts = fileUrl.split('/');
    return `https://${parts[3]}.${parts[2]}/${parts[4]}?v=${new Date().getTime()}`;
  }, [fileUrl]);

  // 1) Fetch the PDF, measure every page and derive each page's px<->pt scale.
  useEffect(() => {
    if (!freshUrl) return undefined;
    let cancelled = false;
    let pdf = null;

    (async () => {
      setRendering(true);
      setPages([]);
      try {
        const buffer = await fetch(freshUrl).then((res) => res.arrayBuffer());
        if (cancelled) return;
        pdfBytesRef.current = buffer;

        // pdfjs transfers the typed array to its worker, so hand it a private copy.
        pdf = await pdfjsLib.getDocument({ data: new Uint8Array(buffer.slice(0)) }).promise;
        if (cancelled) return;

        const avail = Math.min(
          containerRef.current?.clientWidth || MAX_RENDER_WIDTH,
          MAX_RENDER_WIDTH
        );

        const dims = [];
        const proxies = [];
        for (let i = 1; i <= pdf.numPages; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          const p = await pdf.getPage(i);
          if (cancelled) return;
          proxies.push(p);
          const base = p.getViewport({ scale: 1 });
          const scale = avail / base.width;
          const viewport = p.getViewport({ scale });
          dims.push({
            scale,
            pageWidthPt: base.width,
            pageHeightPt: base.height,
            canvasW: Math.round(viewport.width),
            canvasH: Math.round(viewport.height),
          });
        }

        pageProxiesRef.current = proxies;
        setActivePage(0);
        setPosition({ top: 10, left: 10 });
        setPages(dims);
      } catch (error) {
        if (!cancelled) console.error('No se pudo cargar el PDF para la etiqueta:', error);
        if (!cancelled) setRendering(false);
      }
    })();

    return () => {
      cancelled = true;
      if (pdf) pdf.destroy();
    };
  }, [freshUrl]);

  // 2) Once the canvases exist (pages state set), paint each page into its canvas.
  useEffect(() => {
    if (!pages.length) return undefined;
    let cancelled = false;
    const tasks = [];

    (async () => {
      for (let i = 0; i < pages.length; i += 1) {
        const canvas = canvasRefs.current[i];
        const proxy = pageProxiesRef.current[i];
        if (!canvas || !proxy) continue; // eslint-disable-line no-continue
        const viewport = proxy.getViewport({ scale: pages[i].scale });
        canvas.width = pages[i].canvasW;
        canvas.height = pages[i].canvasH;
        const task = proxy.render({ canvasContext: canvas.getContext('2d'), viewport });
        tasks.push(task);
        try {
          // eslint-disable-next-line no-await-in-loop
          await task.promise;
        } catch (error) {
          if (!cancelled) return; // render cancelled
        }
        if (cancelled) return;
      }
      if (!cancelled) setRendering(false);
    })();

    return () => {
      cancelled = true;
      tasks.forEach((t) => t.cancel());
    };
  }, [pages]);

  const clampPosition = (left, top, pageDims = active) => {
    if (!pageDims) return { left, top };
    const w = pageDims.canvasW * LABEL_WIDTH_RATIO;
    const h = w * LABEL_ASPECT;
    const maxLeft = Math.max(0, pageDims.canvasW - w);
    const maxTop = Math.max(0, pageDims.canvasH - h);
    return {
      left: Math.min(Math.max(0, left), maxLeft),
      top: Math.min(Math.max(0, top), maxTop),
    };
  };

  const handleLabelMouseDown = (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    const boxRect = evt.currentTarget.getBoundingClientRect();
    grabOffset.current = {
      x: evt.clientX - boxRect.left,
      y: evt.clientY - boxRect.top,
    };
    setDragging(true);
  };

  const handleMouseMove = (evt) => {
    if (!dragging) return;
    const stage = stageRefs.current[activePage];
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    const left = evt.clientX - rect.left - grabOffset.current.x;
    const top = evt.clientY - rect.top - grabOffset.current.y;
    setPosition(clampPosition(left, top));
  };

  // Click anywhere on a page to drop the label there (and make it the target page).
  const handlePageMouseDown = (idx, evt) => {
    const rect = stageRefs.current[idx]?.getBoundingClientRect();
    if (!rect) return;
    const dims = pages[idx];
    const w = dims.canvasW * LABEL_WIDTH_RATIO;
    const h = w * LABEL_ASPECT;
    const left = evt.clientX - rect.left - w / 2;
    const top = evt.clientY - rect.top - h / 2;
    setActivePage(idx);
    setPosition(clampPosition(left, top, dims));
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const stop = () => setDragging(false);
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, [dragging]);

  const handleAddQR = async () => {
    if (!active || !pdfBytesRef.current) return;
    setLoading(true);

    // Add the cloned element to the DOM invisibly
    const clonedElement = labelQRRef.current.cloneNode(true);
    clonedElement.style.position = 'absolute';
    clonedElement.style.top = '-9999px';
    clonedElement.style.left = '-9999px';
    clonedElement.querySelectorAll('p, span').forEach((el) => {
      el.style.fontWeight = 'bold';
    });
    document.body.appendChild(clonedElement);

    const pdfDoc = await PDFDocument.load(pdfBytesRef.current);
    const targetPage = pdfDoc.getPages()[activePage];

    html2canvas(clonedElement).then(async (canvas) => {
      let img = canvas.toDataURL('image/png');
      img = img.split(',')[1];
      img = Uint8Array.from(atob(img), (char) => char.charCodeAt(0));
      img = await pdfDoc.embedPng(img);

      const { scale, pageHeightPt } = active;
      // Label size in PDF points (matches the on-screen box: labelW px / scale).
      const imgWidth = labelW / scale;
      const imgHeight = imgWidth * LABEL_ASPECT;
      // Convert the on-screen px position into PDF points (origin bottom-left).
      const imgX = position.left / scale;
      const imgY = pageHeightPt - (position.top / scale) - imgHeight;

      targetPage.drawImage(img, {
        x: imgX,
        y: imgY,
        width: imgWidth,
        height: imgHeight,
      });

      document.body.removeChild(clonedElement);
      const modifiedPdfBytes = await pdfDoc.save();
      const file = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
      const stored = await handleFileUpload({ target: { files: [file] } }, false);
      setLoading(false);
      // A refused upload (radicado finalized or annulled meanwhile) leaves the
      // PDF without the tag; don't record it as applied.
      if (stored !== false && onFileLoaded) onFileLoaded();
    });
  };

  // Let the parent (FiledForm) drive "Aplicar etiqueta" from its bottom action bar.
  useImperativeHandle(ref, () => ({
    applyTag: handleAddQR,
    canApply: () => !rendering && !!active,
  }));

  return (
    <Box
      ref={containerRef}
      onMouseMove={handleMouseMove}
      sx={{
        // Fills the bounded, viewport-height panel that FiledForm wraps this in, so its
        // internal scroll keeps the parent's floating action bar visible at the bottom.
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        backgroundColor: '#f5f5f5',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 1,
        // Leave room at the bottom so the last page isn't hidden behind the action bar.
        pb: 8,
      }}
    >
      {/* Instruction banner */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 4,
          backgroundColor: 'rgba(0,0,0,0.72)',
          color: '#fff',
          py: 0.75,
          px: 1.75,
          borderRadius: 4,
          fontSize: 13,
          mb: 1,
          textAlign: 'center',
        }}
      >
        Haz clic en la página y arrastra la etiqueta a su posición, luego pulsa «Aplicar»
      </Box>

      {rendering && (
        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      )}

      {/* Every page; the draggable label is overlaid 1:1 on the active page. */}
      {pages.map((dims, idx) => (
        <Box key={idx} sx={{ mb: 1.5, display: rendering ? 'none' : 'block' }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mb: 0.25 }}
          >
            Página {idx + 1}
            {idx === activePage ? ' · etiqueta aquí' : ''}
          </Typography>
          <Box
            ref={(el) => { stageRefs.current[idx] = el; }}
            onMouseDown={(evt) => handlePageMouseDown(idx, evt)}
            sx={{
              position: 'relative',
              width: dims.canvasW,
              height: dims.canvasH,
              boxShadow: idx === activePage
                ? '0 0 0 2px #2167b2, 0 0 6px rgba(0,0,0,0.25)'
                : '0 0 6px rgba(0,0,0,0.25)',
              cursor: dragging ? 'grabbing' : 'crosshair',
            }}
          >
            <canvas
              ref={(el) => { canvasRefs.current[idx] = el; }}
              style={{ display: 'block' }}
            />

            {idx === activePage && active && (
              <div
                onMouseDown={handleLabelMouseDown}
                style={{
                  position: 'absolute',
                  top: position.top,
                  left: position.left,
                  width: labelW,
                  height: labelH,
                  overflow: 'hidden',
                  opacity: 0.85,
                  border: '2px dashed #2167b2',
                  borderRadius: 4,
                  cursor: dragging ? 'grabbing' : 'grab',
                  backgroundColor: '#fff',
                  userSelect: 'none',
                }}
              >
                <div
                  style={{
                    transform: `scale(${labelW / 450})`,
                    transformOrigin: 'top left',
                    pointerEvents: 'none',
                  }}
                >
                  {labelPreview}
                </div>
              </div>
            )}
          </Box>
        </Box>
      ))}

    </Box>
  );
});

export default FiledInsertQR;
